import { MessageType, useDeleteMessageTypeMutation } from 'api/MessageTypeAPI';
import { SubscriptionPolicy } from 'modules/graphql';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { ApiError } from 'services/api';
import { BUTTON_SIZES, DestructiveButton } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';

export const DELETE_MESSAGE_TYPE_TEXT = {
  DISCLAIMER_TEXT: 'This cannot be undone.',
  ERROR_MESSAGE: 'Unable to delete message type.',
  HEADER_TEXT: 'Delete message type?',
  MODAL_ID: 'delete-message-type-modal',
  SUBMIT_BUTTON_LABEL: 'Delete message type',
  SUCCESS_MESSAGE: 'Success! Deleted:',
  VERIFY_TEXT: 'Are you sure you want to delete',
  DOI_VERIFY_PRETEXT:
    'Please ensure there are no users whose subscription status is currently pending for this message type.',
};

type Props = {
  messageType: MessageType;
  onClose: () => void;
  refetchMessageTypes: () => void;
};

export const DeleteMessageTypeModal = ({ messageType, onClose, refetchMessageTypes }: Props) => {
  const dispatch = useDispatch();
  const isDoubleOptInType = messageType.subscriptionPolicy === SubscriptionPolicy.DoubleOptIn;

  const { isLoading, mutate } = useDeleteMessageTypeMutation({
    onSuccess: () => {
      onClose();
      dispatch(
        addPositiveToast(
          <>
            {DELETE_MESSAGE_TYPE_TEXT.SUCCESS_MESSAGE} <strong>{messageType.name}</strong>
          </>,
        ),
      );
      refetchMessageTypes();
    },
    onError: (e: unknown) => {
      const { response } = e as ApiError;
      const message = response?.data?.message || DELETE_MESSAGE_TYPE_TEXT.ERROR_MESSAGE;
      onClose();
      dispatch(addNegativeToastWithErrorId(message, 'deleteMessageType', e));
    },
  });

  const handleSubmit = () => {
    if (!messageType.id) return null;
    mutate({ messageTypeId: messageType.id });
  };

  return (
    <Modal
      id={DELETE_MESSAGE_TYPE_TEXT.MODAL_ID}
      isModalOpen
      onClose={onClose}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DELETE}
    >
      <ModalHeader>{DELETE_MESSAGE_TYPE_TEXT.HEADER_TEXT}</ModalHeader>
      <ModalBody>
        {isDoubleOptInType && (
          <>
            {DELETE_MESSAGE_TYPE_TEXT.DOI_VERIFY_PRETEXT}
            <br />
            <br />
          </>
        )}
        {DELETE_MESSAGE_TYPE_TEXT.VERIFY_TEXT} <strong>{messageType.name}</strong>?{' '}
        {DELETE_MESSAGE_TYPE_TEXT.DISCLAIMER_TEXT}
      </ModalBody>
      <ModalFooter onClose={onClose}>
        <DestructiveButton
          data-test="delete-message-type-btn"
          onClick={handleSubmit}
          isLoading={isLoading}
          size={BUTTON_SIZES.LARGE}
        >
          {DELETE_MESSAGE_TYPE_TEXT.SUBMIT_BUTTON_LABEL}
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
